import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TextField,
  Autocomplete,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import {
  setResultVisualizationExpFilter,
  setResultVisualizationFilter,
} from "../../store/filterSlice";
import { RootState } from "../../store/store";
import { resultVisualisationFilterExperimentaionApi } from "../../api/forecastPageApi";
import { useOktaAuth } from "@okta/okta-react";
import { setExperimentId } from "../../store/resultVisualizationExperimentation";
import loadingSvg from "../../images/bouncing-circles.svg";
import { useForecastClusterFilter } from "../../api/filtersApi";
import { setExperimentLoader } from '../../store/lagDropdownSlice';

const ExperimentationId: React.FC<{ lag?: boolean }> = ({ lag }) => {
  const dispatch = useDispatch();
  const { authState } = useOktaAuth();
  const [experimentIds, setExperimentIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState(false);

  const [selectedExperimentIds, setSelectedExperimentIds] =
    useState<string>("");
  const filter = useSelector(
    (state: RootState) => state.resultVisualizationFilter
  );
  const expData = useSelector(
    (state: RootState) => state.resultVisualizationExperimentationData
  );

  const userData = useSelector((state: RootState) => state.user.currentSector);
    const experimentationPayload = useMemo(() => {
      return {
        shortTermMidTermCode: filter?.term?.toString(),
        sector: userData?.sector,
        market: userData?.market,
      };
    }, [userData, filter]);
  const { data: clusterApiData, refetch: refetchCluster } =
    useForecastClusterFilter(
      authState?.accessToken?.accessToken!,
      "cluster",
      userData ? userData : {}
    );

    const isReady = !!(userData?.sector && userData?.market && filter?.term);

    useEffect(() => {

      if (!isReady || isLoading || hasFetched) return;
      dispatch(
        setResultVisualizationFilter({
          key: "clusters",
          value: clusterApiData?.clusters,
      }));
      const fetchExperimentationData = async () => {
        if(isLoading) return;
        setIsLoading(true);
        dispatch(setExperimentLoader(true));
        try {
          const data = await resultVisualisationFilterExperimentaionApi(
            experimentationPayload,
            authState
          );
          
          dispatch(setExperimentId(data));
          const maskZero = data?.experimentIds?.filter((id:string) => id !== '0');
          setExperimentIds(maskZero || []);
          setHasFetched(true);
        } catch (error) {
          console.error("Experimentation API error:", error);
        } finally {
          setIsLoading(false);
          dispatch(setExperimentLoader(false));
        }
      };
      fetchExperimentationData();

    }, [isReady, isLoading, hasFetched]);

  useEffect(() => {
    setSelectedExperimentIds(experimentIds && experimentIds?.length > 0 ? experimentIds[0] : "");
    dispatch(setResultVisualizationExpFilter({
      key: "experimentId",
      value: [experimentIds[0] ?? ""],
    }))
  }, [experimentIds]);

  useEffect(() => {
    setHasFetched(false);
  }, [userData?.sector, userData?.market, filter?.term]);
  
  const handleSelectionChange = (
    event: React.SyntheticEvent,
    value: string | null
  ) => {
    const payloadValue = [value ?? ""];
    setSelectedExperimentIds(value ?? "");
    dispatch(
      setResultVisualizationExpFilter({
        key: "experimentId",
        value: payloadValue,
      })
    );
  };

  const darkTheme = createTheme({
    palette: {
      mode: "dark",
      background: {
        paper: "#272727",
      },
      text: {
        primary: "#ffffff",
      },
    },
  });

  return (
    <ThemeProvider theme={darkTheme}>
      <div className="p-2 bg-secondary-bg rounded h-64">
        <div className="flex justify-between">
          <h3 className="m-2 p-1 font-bold">Experiment ID</h3>
        </div>
        {expData?.loader || isLoading ? (
          <div className="flex justify-center">
            <img
              src={loadingSvg}
              alt="loading"
              className="mt-10"
              width={40}
              height={40}
            />
          </div>
        ) : (
          <Autocomplete
            className="p-2"
            options={experimentIds}
            value={selectedExperimentIds}
            onChange={(event, value) => handleSelectionChange(event, value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{params.InputProps.endAdornment}</>,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    borderColor: "#616161",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#909090",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#616161",
                  },
                }}
              />
            )}
            sx={{
              "& .MuiAutocomplete-popupIndicator": {
                color: "white",
              },
              "& .MuiAutocomplete-clearIndicator": {
                color: "white",
              },
            }}
          />
        )}
      </div>
    </ThemeProvider>
  );
};

export default ExperimentationId;
